import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './WettkaempferRegistrieren.css';
import API_CONFIG from '../../config/api';

const STEPS = [
  { id: 'profil',     label: 'Profil',     num: 1 },
  { id: 'turniere',   label: 'Turniere & Divisionen', num: 2 },
  { id: 'warenkorb',  label: 'Warenkorb',  num: 3 },
  { id: 'abschluss',  label: 'Abschluss',  num: 4 },
];

const LEER_PROFIL = {
  wettkaempfer_id: null,
  vorname: '', nachname: '', email: '', telefon: '',
  geburtsdatum: '', geschlecht: '',
  gewicht: '', skill_level_kata: '', skill_level_grappling: '',
  adresse: { strasse: '', plz: '', ort: '', land: 'Deutschland' }
};

/* ── Filterlogik ──────────────────────────────────────────────────────────── */

// Typen bei denen Ältere auch bei Jüngeren starten dürfen
const KAMPF_TYPEN = new Set([
  'Kumite', 'Continous Fighting', 'Pointfighting',
  'K1', 'Fullcontact', 'Grappling', 'Bruchtest', 'Rumble'
]);

// Typen mit Formen-Regel (eigenes Level + 1 höher erlaubt)
const FORMEN_TYPEN = new Set(['Formen', 'Selbstverteidigung']);

// Skill-Level-Reihenfolge (aufsteigend)
const SKILL_ORDER = [
  'Beginner', 'Rookie Grappling', 'Amateur',
  'Intermediate', 'Advanced', 'Advanced Grappling',
  'Master Grappling', 'Black Belts', 'Grand Champion',
];

const SKILL_IDX = Object.fromEntries(SKILL_ORDER.map((s, i) => [s, i]));

// Tab-Gruppen für die Divisions-Auswahl
const DIV_TABS = [
  { id: 'alle',            label: 'Alle',             types: null },
  { id: 'formen',          label: 'Formen',            types: ['Formen'] },
  { id: 'selbst',          label: 'Selbstverteidigung',types: ['Selbstverteidigung'] },
  { id: 'kumite',          label: 'Kumite',            types: ['Kumite'] },
  { id: 'kickboxen',       label: 'Kickboxen',         types: ['Continous Fighting', 'Pointfighting', 'K1', 'Fullcontact'] },
  { id: 'grappling',       label: 'Grappling',         types: ['Grappling'] },
  { id: 'bruchtest',       label: 'Bruchtest',         types: ['Bruchtest'] },
  { id: 'rumble',          label: 'Rumble',            types: ['Rumble'] },
];

// Sub-Kategorie-Filter innerhalb eines Haupt-Tabs
const SUB_FILTERS = {
  formen: [
    { id: 'alle',     label: 'Alle' },
    { id: 'japanese', label: 'Japanese',     match: n => /japanese/i.test(n) },
    { id: 'korean',   label: 'Korean',       match: n => /korean/i.test(n) },
    { id: 'chinese',  label: 'Chinese/Kenpo',match: n => /chinese|kenpo|kempo/i.test(n) },
    { id: 'weapons',  label: 'Weapons',      match: n => /weapon|bladed/i.test(n) },
    { id: 'creative', label: 'Open/Creative',match: n => /open|creative|music|musik/i.test(n) },
    { id: 'duo',      label: 'Duo',          match: n => /duo/i.test(n) },
    { id: 'team',     label: 'Team',         match: n => /\bteam\b/i.test(n) && !/duo/i.test(n) },
  ],
  kickboxen: [
    { id: 'alle',    label: 'Alle' },
    { id: 'point',   label: 'Pointfighting', match: (n, typ) => typ === 'Pointfighting' },
    { id: 'continu', label: 'Continous',     match: (n, typ) => typ === 'Continous Fighting' },
    { id: 'k1',      label: 'K1',            match: (n, typ) => typ === 'K1' },
    { id: 'full',    label: 'Fullcontact',   match: (n, typ) => typ === 'Fullcontact' },
  ],
  selbst: [
    { id: 'alle',  label: 'Alle' },
    { id: 'einzel',label: 'Einzel',match: n => !/team/i.test(n) },
    { id: 'team',  label: 'Team', match: n => /team/i.test(n) },
  ],
};

// Prüfen ob eine Division eine Duo/Team-Anmeldung erfordert
function isDuoOrTeam(div) {
  const n = (div.name || div.Division_Name || '').toLowerCase();
  if (/\bduo\b/i.test(n)) return 'duo';
  if (/\bteam\b/i.test(n)) return 'team';
  return null;
}

function matchesGender(geschlecht, divGender) {
  if (!divGender || divGender === 'Male/Female') return true;
  if ((geschlecht === 'männlich' || geschlecht === 'male')   && divGender === 'Male')   return true;
  if ((geschlecht === 'weiblich' || geschlecht === 'female') && divGender === 'Female') return true;
  return false;
}

function matchesAge(age, div, isKampf) {
  const from = parseInt(div.Age_from) || 0;
  const to   = parseInt(div.Age_to)   || 999;
  if (age === null) return true;
  if (isKampf) {
    // Ältere dürfen bei Jüngeren starten → nur untere Grenze prüfen,
    // aber nicht zu alt (max. 8 Jahre über dem Gruppenmaximum)
    return age >= from && age <= to + 8;
  }
  return age >= from && age <= to;
}

function matchesWeight(weight, div) {
  if (!weight) return true;
  const min = parseFloat(div.Minimun_Weight) || 0;
  const max = parseFloat(div.Maximum_Weight) || 999;
  return weight >= min && weight <= max;
}

function matchesSkillLevel(athleteSkill, divSkill, divType) {
  if (!athleteSkill || !divSkill) return true;
  if (FORMEN_TYPEN.has(divType)) {
    // Eigenes Level + ein Level höher
    const ai = SKILL_IDX[athleteSkill] ?? -1;
    const di = SKILL_IDX[divSkill]     ?? -1;
    if (ai === -1 || di === -1) return divSkill === athleteSkill;
    return di === ai || di === ai + 1;
  }
  if (divType === 'Grappling') {
    return divSkill === athleteSkill;
  }
  // Alle anderen Typen: kein harter Skill-Filter
  return true;
}

function filterForAthlete(allDiv, profil) {
  const age    = profil.geburtsdatum
    ? Math.floor((Date.now() - new Date(profil.geburtsdatum)) / (365.25 * 864e5))
    : null;
  const weight = profil.gewicht ? parseFloat(profil.gewicht) : null;

  // Wenn gar keine Profildaten → ungefiltert
  if (!profil.geschlecht && age === null && !weight && !profil.skill_level_kata && !profil.skill_level_grappling) return allDiv;

  return allDiv.filter(div => {
    const typ     = div.Division_Type || '';
    const isKampf = KAMPF_TYPEN.has(typ);
    const hasWeight = div.Minimun_Weight && parseFloat(div.Minimun_Weight) > 0;

    // 1. Geschlecht
    if (profil.geschlecht && !matchesGender(profil.geschlecht, div.Gender)) return false;

    // 2. Alter
    if (age !== null && (div.Age_from || div.Age_to)) {
      if (!matchesAge(age, div, isKampf)) return false;
    }

    // 3. Gewicht – nur wenn Division eine Gewichtsklasse hat
    if (hasWeight && weight) {
      if (!matchesWeight(weight, div)) return false;
    }

    // 4a. Skill-Level für Formen/Selbstverteidigung
    if (profil.skill_level_kata && div.Skill_Level && FORMEN_TYPEN.has(typ)) {
      if (!matchesSkillLevel(profil.skill_level_kata, div.Skill_Level, typ)) return false;
    }

    // 4b. Skill-Level für Grappling
    if (profil.skill_level_grappling && div.Skill_Level && typ === 'Grappling') {
      if (!matchesSkillLevel(profil.skill_level_grappling, div.Skill_Level, typ)) return false;
    }

    return true;
  });
}

// Preis einer Division berechnen basierend auf Turnier-Einstellungen
function berechnePreis(division, turnier, anzahlEinzelBisher) {
  const isTeam = division.team === 1 || division.team === true ||
    (division.name || '').toLowerCase().includes('team');

  const heute = new Date();
  const fruehbucherBis = turnier.fruehbucher_bis ? new Date(turnier.fruehbucher_bis) : null;
  const isFruehbucher = fruehbucherBis && heute <= fruehbucherBis;

  let basispreis = isTeam
    ? parseFloat(turnier.preis_team || 40)
    : parseFloat(turnier.preis_einzel || 25);

  // Rabatt für 2. / weitere Einzeldivisionen
  if (!isTeam) {
    if (anzahlEinzelBisher === 1) {
      const rabatt = parseFloat(turnier.rabatt_zweite_kategorie || 0) / 100;
      basispreis = basispreis * (1 - rabatt);
    } else if (anzahlEinzelBisher >= 2) {
      const rabatt = parseFloat(turnier.rabatt_weitere_kategorien || 0) / 100;
      basispreis = basispreis * (1 - rabatt);
    }
  }

  // Frühbucher-Rabatt
  if (isFruehbucher) {
    const rabatt = parseFloat(turnier.fruehbucher_rabatt || 0) / 100;
    basispreis = basispreis * (1 - rabatt);
  }

  return Math.round(basispreis * 100) / 100;
}

export default function WettkaempferRegistrieren() {
  const [step,       setStep]       = useState(0);
  const [profil,     setProfil]     = useState({ ...LEER_PROFIL, adresse: { ...LEER_PROFIL.adresse } });
  // { turnierId: [ normalizedDiv, ... ] }
  const [divSelections, setDivSelections] = useState({});
  // [{ turnier_id, name, ... }]
  const [selTurniere, setSelTurniere] = useState([]);
  // Warenkorb: [{ profil, anmeldungen: [{ turnier, divisionen }] }]
  const [warenkorb,     setWarenkorb]     = useState([]);
  const [submitting,    setSubmitting]    = useState(false);
  const [submitResult,  setSubmitResult]  = useState(null); // null | { ok: [], errors: [] }

  const handleProfilChange  = (field, value) => setProfil(p => ({ ...p, [field]: value }));
  const handleAdresseChange = (field, value) => setProfil(p => ({ ...p, adresse: { ...p.adresse, [field]: value } }));

  const toggleTurnier = (turnier) => {
    setSelTurniere(prev => {
      const exists = prev.some(t => t.turnier_id === turnier.turnier_id);
      if (exists) {
        // Divisionen-Auswahl für dieses Turnier löschen
        setDivSelections(ds => {
          const updated = { ...ds };
          delete updated[turnier.turnier_id];
          return updated;
        });
        return prev.filter(t => t.turnier_id !== turnier.turnier_id);
      }
      return [...prev, turnier];
    });
  };

  const toggleDiv = (turnierId, div) => {
    setDivSelections(prev => {
      const current = prev[turnierId] || [];
      const exists  = current.some(d => d.id === div.id);
      return {
        ...prev,
        [turnierId]: exists ? current.filter(d => d.id !== div.id) : [...current, div]
      };
    });
  };

  const buildAnmeldungen = () =>
    selTurniere.map(turnier => ({
      turnier,
      divisionen: (divSelections[turnier.turnier_id] || []).map((div, i) => {
        const anzahlEinzelBisher = (divSelections[turnier.turnier_id] || [])
          .slice(0, i)
          .filter(d => !(d.team === 1 || (d.name || '').toLowerCase().includes('team')))
          .length;
        return { ...div, preis: berechnePreis(div, turnier, anzahlEinzelBisher) };
      })
    })).filter(a => a.divisionen.length > 0);

  const addToWarenkorb = () => {
    const anmeldungen = buildAnmeldungen();
    if (anmeldungen.length === 0) return;
    setWarenkorb(wk => [...wk, {
      profil: { ...profil, adresse: { ...profil.adresse } },
      anmeldungen
    }]);
    setProfil({ ...LEER_PROFIL, adresse: { ...LEER_PROFIL.adresse } });
    setSelTurniere([]);
    setDivSelections({});
    setStep(2);
  };

  const removeFromWarenkorb = (idx) => setWarenkorb(wk => wk.filter((_, i) => i !== idx));

  const submitWarenkorb = async () => {
    setSubmitting(true);
    setSubmitResult(null);
    const token = localStorage.getItem('token');

    // JWT-Payload dekodieren → vereins_id holen
    let myVereinId = null;
    try { myVereinId = JSON.parse(atob(token.split('.')[1])).vereins_id; } catch {}

    const ok = [];
    const errors = [];

    for (const entry of warenkorb) {
      let wkId = entry.profil.wettkaempfer_id;
      const name = `${entry.profil.vorname} ${entry.profil.nachname}`;
      try {
        // Profil-Payload (gemeinsam für POST und PUT)
        const profilPayload = {
          vorname:           entry.profil.vorname,
          nachname:          entry.profil.nachname,
          geburtsdatum:      entry.profil.geburtsdatum,
          geschlecht:        entry.profil.geschlecht,
          gewicht:           entry.profil.gewicht ? parseFloat(entry.profil.gewicht) : 0,
          email:             entry.profil.email   || null,
          handy:             entry.profil.telefon || null,
          vereins_id:        myVereinId,
          skill_level:       entry.profil.skill_level_kata || entry.profil.skill_level_grappling || null,
          skill_level_kata:  entry.profil.skill_level_kata      || null,
          skill_level_grappling: entry.profil.skill_level_grappling || null,
        };

        // 1. Neuen Athleten anlegen oder bestehenden aktualisieren
        if (!wkId) {
          const res = await fetch('/api/wettkaempfer', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(profilPayload),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Athlet konnte nicht angelegt werden');
          wkId = data.data?.wettkaempfer_id;
          if (!wkId) throw new Error('Keine Wettkämpfer-ID erhalten');
        } else {
          // Bestehenden Athleten aktualisieren (Gewicht, Skill-Level usw.)
          await fetch(`/api/wettkaempfer/${wkId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(profilPayload),
          }).catch(() => {}); // Fehler beim Update sind nicht kritisch
        }

        // 2. Pro Turnier anmelden (inkl. Duo/Team-Partner)
        for (const anm of entry.anmeldungen) {
          // Partner-IDs aus Duo/Team-Divisionen sammeln
          const partnerIds = [];
          anm.divisionen.forEach(d => {
            (d.partnerIds || []).forEach(pid => {
              if (pid && !partnerIds.includes(pid)) partnerIds.push(pid);
            });
          });

          const allWkIds = [wkId, ...partnerIds];

          // Haupt-Kämpfer bekommt alle Divisionen
          const wettkampferDiv = [
            {
              wettkaempfer_id: wkId,
              divisionen: anm.divisionen.map(d => ({
                division_code: d.code || d.id,
                startgebuehr:  d.preis,
              })),
            },
            // Partner bekommen nur die Divisionen in denen sie stehen
            ...partnerIds.map(pid => ({
              wettkaempfer_id: pid,
              divisionen: anm.divisionen
                .filter(d => (d.partnerIds || []).includes(pid))
                .map(d => ({ division_code: d.code || d.id, startgebuehr: d.preis })),
            })),
          ];

          const res = await fetch('/api/anmeldungen', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              turnier_id:             anm.turnier.turnier_id,
              verein_id:              myVereinId,
              wettkampfer_ids:        allWkIds,
              wettkampfer_divisionen: wettkampferDiv,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(`${anm.turnier.name}: ${data.error || 'Anmeldung fehlgeschlagen'}`);
          ok.push({ name, turnier: anm.turnier.name, divisionen: anm.divisionen.length });
        }
      } catch (err) {
        errors.push({ name, error: err.message });
      }
    }

    setSubmitting(false);
    const result = { ok, errors };
    setSubmitResult(result);
    if (errors.length === 0) setWarenkorb([]);
  };

  const resetForNewFighter = () => {
    setProfil({ ...LEER_PROFIL, adresse: { ...LEER_PROFIL.adresse } });
    setSelTurniere([]);
    setDivSelections({});
    setWarenkorb([]);
    setSubmitResult(null);
    setStep(0);
  };

  const gesamtpreis = warenkorb.reduce((s, e) =>
    s + e.anmeldungen.reduce((ss, a) =>
      ss + a.divisionen.reduce((sss, d) => sss + d.preis, 0), 0), 0);

  const hasPendingAnmeldung = selTurniere.length > 0 &&
    selTurniere.some(t => (divSelections[t.turnier_id] || []).length > 0);

  const canNext = () => {
    if (step === 0) return !!(profil.vorname && profil.nachname && profil.geburtsdatum && profil.geschlecht);
    if (step === 1) return hasPendingAnmeldung;
    if (step === 2) return warenkorb.length > 0;
    return true;
  };

  return (
    <div className="wr-container">
      <div className="wr-header">
        <h2>Wettkämpfer registrieren</h2>
        <p>Sportler für Turniere und Divisionen anmelden</p>
      </div>

      {/* Step Progress */}
      <div className="wr-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              className={`wr-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
            >
              <span className="wr-step-num">{i < step ? '✓' : s.num}</span>
              <span className="wr-step-label">{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`wr-step-line ${i < step ? 'done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="wr-content">
        {step === 0 && (
          <ProfilStep profil={profil} onChange={handleProfilChange} onAdresseChange={handleAdresseChange} />
        )}
        {step === 1 && (
          <TurniereDivisionenStep
            profil={profil}
            selTurniere={selTurniere}
            divSelections={divSelections}
            onToggleTurnier={toggleTurnier}
            onToggleDiv={toggleDiv}
          />
        )}
        {step === 2 && (
          <WarenkorbStep
            warenkorb={warenkorb}
            pendingProfil={profil}
            pendingAnmeldungen={buildAnmeldungen()}
            hasPending={hasPendingAnmeldung}
            onRemove={removeFromWarenkorb}
            onAddPending={hasPendingAnmeldung ? addToWarenkorb : null}
            onAddAnother={() => { setStep(0); }}
            gesamtpreis={gesamtpreis}
          />
        )}
        {step === 3 && (
          <AbschlussStep warenkorb={warenkorb} gesamtpreis={gesamtpreis} submitResult={submitResult} onNewFighter={resetForNewFighter} />
        )}
      </div>

      <div className="wr-footer">
        <button className="wr-btn-ghost" onClick={() => setStep(s => s - 1)} disabled={step === 0}>
          ← Zurück
        </button>
        <div className="wr-footer-dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`wr-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} />
          ))}
        </div>
        {step < STEPS.length - 1 ? (
          <button className="wr-btn-primary" onClick={() => {
            if (step === 1 && hasPendingAnmeldung) { addToWarenkorb(); }
            else setStep(s => s + 1);
          }} disabled={!canNext()}>
            {step === 1 ? '+ Zum Warenkorb' : 'Weiter →'}
          </button>
        ) : (
          <button className="wr-btn-primary"
            disabled={warenkorb.length === 0 || submitting || submitResult?.errors?.length === 0}
            onClick={submitWarenkorb}>
            {submitting ? '⟳ Wird gespeichert…' : submitResult ? '✓ Erneut senden' : '✓ Registrierung abschließen'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   Schritt 1 — Profil
   ───────────────────────────────────────────────── */
function ProfilStep({ profil, onChange, onAdresseChange }) {
  const [searchTerm,  setSearchTerm]  = useState('');
  const [results,     setResults]     = useState([]);
  const [searching,   setSearching]   = useState(false);
  const [open,        setOpen]        = useState(false);
  const [skillGroups, setSkillGroups] = useState({ kata: [], grappling: [] });
  const timerRef = useRef(null);

  // Skill-Gruppen vom Backend laden
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/divisionen/skill-groups', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.success) setSkillGroups(d.groups); })
      .catch(() => {});
  }, []);

  const age = (d) => Math.floor((Date.now() - new Date(d)) / (365.25 * 864e5));

  const doSearch = async (term) => {
    if (term.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/wettkaempfer?search=${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const list = data.success ? data.data : Array.isArray(data) ? data : data.sportler || [];
        setResults(Array.isArray(list) ? list : []);
      }
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(v), 280);
  };

  const select = (s) => {
    onChange('wettkaempfer_id', s.wettkaempfer_id || s.id || null);
    onChange('vorname',      s.vorname      || '');
    onChange('nachname',     s.nachname     || '');
    onChange('email',        s.email        || '');
    onChange('telefon',      s.telefon || s.handy || '');
    onChange('geburtsdatum', s.geburtsdatum ? String(s.geburtsdatum).slice(0, 10) : '');
    onChange('geschlecht',   s.geschlecht   || '');
    onChange('gewicht',      String(s.gewicht || ''));
    // skill_level aus DB (enum: Beginner/Intermediate/Advanced/Black Belt) → kata-Level
    const dbSkill = s.skill_level || '';
    const grapplingLvls = ['Rookie Grappling', 'Advanced Grappling', 'Master Grappling'];
    if (grapplingLvls.includes(dbSkill)) {
      onChange('skill_level_grappling', dbSkill);
    } else {
      onChange('skill_level_kata', dbSkill);
    }
    if (s.strasse) {
      onAdresseChange('strasse', s.strasse || '');
      onAdresseChange('plz',     s.plz     || '');
      onAdresseChange('ort',     s.ort     || '');
      onAdresseChange('land',    s.land    || 'Deutschland');
    }
    setSearchTerm(`${s.vorname} ${s.nachname}`);
    setResults([]);
    setOpen(false);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div className="wr-step-body">
      <div className="wr-card">
        <div className="wr-card-label">Vorhandenen Sportler suchen</div>
        <div className="wr-search-wrap">
          <span className="wr-search-icon">
            {searching
              ? <span className="wr-spin">⟳</span>
              : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            }
          </span>
          <input className="wr-input wr-search-input" type="text"
            placeholder="Name eingeben (min. 2 Zeichen)…"
            value={searchTerm} onChange={handleInput}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)} />
        </div>
        {open && results.length > 0 && (
          <div className="wr-dropdown">
            {results.map((s) => (
              <div key={s.wettkaempfer_id || s.id} className="wr-dropdown-item" onMouseDown={() => select(s)}>
                <span className="wr-dropdown-name">
                  {s.vorname} {s.nachname}
                  <span className="wr-gender-tag">{s.geschlecht === 'männlich' || s.geschlecht === 'male' ? '♂' : '♀'}</span>
                </span>
                <span className="wr-dropdown-meta">
                  {s.geburtsdatum && <span>{age(s.geburtsdatum)} J.</span>}
                  {s.gewicht && <span>{s.gewicht} kg</span>}
                  {s.gurtfarbe && <span>{s.gurtfarbe}</span>}
                  {s.verein_name && <span>{s.verein_name}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
        {open && searchTerm.length >= 2 && !searching && results.length === 0 && (
          <div className="wr-dropdown wr-dropdown-empty">Kein Sportler gefunden — Formular manuell ausfüllen</div>
        )}
      </div>

      <div className="wr-card">
        <div className="wr-card-label">Persönliche Daten</div>
        <div className="wr-grid">
          <div className="wr-field">
            <label>Vorname <span className="wr-req">*</span></label>
            <input className="wr-input" type="text" value={profil.vorname} placeholder="Max"
              onChange={e => onChange('vorname', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Nachname <span className="wr-req">*</span></label>
            <input className="wr-input" type="text" value={profil.nachname} placeholder="Mustermann"
              onChange={e => onChange('nachname', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Geburtsdatum <span className="wr-req">*</span></label>
            <input className="wr-input" type="date" value={profil.geburtsdatum}
              onChange={e => onChange('geburtsdatum', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Geschlecht <span className="wr-req">*</span></label>
            <select className="wr-input" value={profil.geschlecht} onChange={e => onChange('geschlecht', e.target.value)}>
              <option value="">Bitte wählen</option>
              <option value="männlich">♂ Männlich</option>
              <option value="weiblich">♀ Weiblich</option>
              <option value="divers">Divers</option>
            </select>
          </div>
          <div className="wr-field">
            <label>E-Mail</label>
            <input className="wr-input" type="email" value={profil.email} placeholder="max@example.com"
              onChange={e => onChange('email', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Telefon</label>
            <input className="wr-input" type="tel" value={profil.telefon} placeholder="+49 123 456789"
              onChange={e => onChange('telefon', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Gewicht <span className="wr-optional">(kg)</span></label>
            <input className="wr-input" type="number" step="0.5" min="20" max="200"
              value={profil.gewicht} placeholder="70"
              onChange={e => onChange('gewicht', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Formen-Level <span className="wr-optional">(Formen / Selbstverteidigung)</span></label>
            <select className="wr-input" value={profil.skill_level_kata}
              onChange={e => onChange('skill_level_kata', e.target.value)}>
              <option value="">Kein Filter</option>
              {(skillGroups.kata || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="wr-field">
            <label>Grappling-Level</label>
            <select className="wr-input" value={profil.skill_level_grappling}
              onChange={e => onChange('skill_level_grappling', e.target.value)}>
              <option value="">Kein Filter</option>
              {(skillGroups.grappling || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="wr-card">
        <div className="wr-card-label">Adresse <span className="wr-optional">(optional)</span></div>
        <div className="wr-grid">
          <div className="wr-field wr-col-2">
            <label>Straße &amp; Hausnummer</label>
            <input className="wr-input" type="text" value={profil.adresse.strasse} placeholder="Musterstraße 1"
              onChange={e => onAdresseChange('strasse', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>PLZ</label>
            <input className="wr-input" type="text" value={profil.adresse.plz} placeholder="12345"
              onChange={e => onAdresseChange('plz', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Ort</label>
            <input className="wr-input" type="text" value={profil.adresse.ort} placeholder="Berlin"
              onChange={e => onAdresseChange('ort', e.target.value)} />
          </div>
          <div className="wr-field">
            <label>Land</label>
            <select className="wr-input" value={profil.adresse.land} onChange={e => onAdresseChange('land', e.target.value)}>
              <option>Deutschland</option>
              <option>Österreich</option>
              <option>Schweiz</option>
              <option>Andere</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   Schritt 2 — Turniere & Divisionen
   ───────────────────────────────────────────────── */
function TurniereDivisionenStep({ profil, selTurniere, divSelections, onToggleTurnier, onToggleDiv }) {
  const [turniere,  setTurniere]  = useState([]);
  const [allDiv,    setAllDiv]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [openTId,   setOpenTId]   = useState(null);
  const [search,    setSearch]    = useState('');
  const [activeTab, setActiveTab] = useState('alle');
  const [subFilter, setSubFilter] = useState('alle');
  const [showAll,   setShowAll]   = useState(false);
  const [teamModal, setTeamModal] = useState(null); // { div, turnierId, mode }
  const [teamDaten, setTeamDaten] = useState({});   // { 'turnierId__divId': { teamName, mitglieder } }

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const [tRes, dRes] = await Promise.all([
          fetch('/api/turniere?zukunft=true&limit=50', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/divisionen?limit=2000',           { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (tRes.ok) { const d = await tRes.json(); setTurniere(d.success ? d.data : []); }
        if (dRes.ok) { const d = await dRes.json(); setAllDiv(d.success ? d.data : []); }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const normalize = (d) => {
    const minW = parseFloat(d.Minimun_Weight || 0);
    const maxW = parseFloat(d.Maximum_Weight || 9999);
    const gewicht = (maxW > 0 && maxW < 9999)
      ? (minW > 0 ? `${minW}–${maxW} kg` : `bis ${maxW} kg`)
      : '';
    return {
      id:         d.Division_Code,
      code:       d.Division_Code,
      name:       d.Division_Name || d.Division_Code,
      typ:        d.Division_Type || '',
      alter:      d.Age || (d.Age_from && d.Age_to ? `${d.Age_from}–${d.Age_to}` : ''),
      geschlecht: d.Gender || '',
      skill:      d.Skill_Level || '',
      team:       d.Team || 0,
      gewicht,
    };
  };

  const profilAlter = profil.geburtsdatum
    ? Math.floor((Date.now() - new Date(profil.geburtsdatum)) / (365.25 * 864e5))
    : null;
  const hasProfilFilter = !!(profil.geschlecht || profil.geburtsdatum || profil.gewicht || profil.skill_level_kata || profil.skill_level_grappling);

  const athleteFilteredDiv = showAll ? allDiv : filterForAthlete(allDiv, profil);
  const activeTabDef = DIV_TABS.find(t => t.id === activeTab) || DIV_TABS[0];
  const activeSubTabs = SUB_FILTERS[activeTab] || null;
  const activeSubDef = activeSubTabs?.find(s => s.id === subFilter);

  const filteredDiv = athleteFilteredDiv.filter(d => {
    if (activeTabDef.types && !activeTabDef.types.includes(d.Division_Type)) return false;
    if (subFilter !== 'alle' && activeSubDef?.match) {
      if (!activeSubDef.match(d.Division_Name || '', d.Division_Type || '')) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      return (d.Division_Name || '').toLowerCase().includes(q) ||
             (d.Division_Code  || '').toLowerCase().includes(q);
    }
    return true;
  });

  const handleTabChange = (tabId) => { setActiveTab(tabId); setSubFilter('alle'); };

  const handleDivClick = (turnierId, norm) => {
    const already = (divSelections[turnierId] || []).some(s => s.id === norm.id);
    if (already) {
      // Abwählen → auch teamDaten entfernen
      onToggleDiv(turnierId, norm);
      setTeamDaten(prev => { const n = { ...prev }; delete n[`${turnierId}__${norm.id}`]; return n; });
    } else {
      const mode = isDuoOrTeam(norm);
      if (mode) {
        setTeamModal({ div: norm, turnierId, mode });
      } else {
        onToggleDiv(turnierId, norm);
      }
    }
  };

  const confirmTeamModal = (teamName, mitglieder, partnerIds) => {
    const { div, turnierId } = teamModal;
    const key = `${turnierId}__${div.id}`;
    setTeamDaten(prev => ({ ...prev, [key]: { teamName, mitglieder, partnerIds } }));
    // Embed partnerIds in div so submitWarenkorb can include them in the API call
    const enrichedDiv = partnerIds?.length ? { ...div, partnerIds } : div;
    onToggleDiv(turnierId, enrichedDiv);
    setTeamModal(null);
  };

  const typClass = (typ) => {
    if (!typ) return 'sonstige';
    const t = typ.toLowerCase();
    if (t.includes('form') || t.includes('kata')) return 'kata';
    if (t.includes('kumite') || t.includes('point') || t.includes('continu') || t.includes('fight')) return 'kumite';
    return 'sonstige';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const formatPreis = (p) => parseFloat(p || 0).toFixed(2).replace('.', ',') + ' €';

  if (loading) return <div className="wr-loading" style={{ padding: '3rem', textAlign: 'center' }}>Lade Turniere und Divisionen…</div>;

  return (
    <div className="wr-step-body">
      {/* Anmeldung für */}
      <div className="wr-card">
        <div className="wr-card-label">
          Anmeldung für: <strong>{profil.vorname} {profil.nachname}</strong>
        </div>

        {/* Profil-Filter-Info */}
        {hasProfilFilter && allDiv.length > 0 && (
          <div className="wr-profile-filter-bar">
            <div className="wr-pf-left">
              <span className="wr-pf-label">Gefiltert für</span>
              {profil.geschlecht && (
                <span className="wr-pf-tag">{profil.geschlecht === 'männlich' ? '♂ männlich' : '♀ weiblich'}</span>
              )}
              {profilAlter !== null && <span className="wr-pf-tag">{profilAlter} J.</span>}
              {profil.gewicht && <span className="wr-pf-tag">{profil.gewicht} kg</span>}
              {profil.skill_level_kata && <span className="wr-pf-tag">Formen: {profil.skill_level_kata}</span>}
              {profil.skill_level_grappling && <span className="wr-pf-tag">Grappling: {profil.skill_level_grappling}</span>}
              {!showAll && (
                <span className="wr-pf-count">
                  {athleteFilteredDiv.length} / {allDiv.length} Divisionen
                </span>
              )}
            </div>
            <button className="wr-pf-toggle" onClick={() => setShowAll(v => !v)}>
              {showAll ? '⚡ Nur passende' : `Alle ${allDiv.length} anzeigen`}
            </button>
          </div>
        )}

        <div className="wr-turnier-list">
          {turniere.length === 0 && (
            <div className="wr-loading">Keine aktiven Turniere gefunden.</div>
          )}
          {turniere.map(t => {
            const sel    = selTurniere.some(st => st.turnier_id === t.turnier_id);
            const divCnt = (divSelections[t.turnier_id] || []).length;
            const isFruh = t.fruehbucher_bis && new Date() <= new Date(t.fruehbucher_bis);
            return (
              <div key={t.turnier_id} className={`wr-turnier-row ${sel ? 'selected' : ''}`}>
                <div className="wr-turnier-check-area" onClick={() => onToggleTurnier(t)}>
                  <span className={`wr-checkbox ${sel ? 'checked' : ''}`}>{sel ? '✓' : ''}</span>
                  <div className="wr-turnier-info">
                    <div className="wr-turnier-name">
                      {t.name}
                      {isFruh && <span className="wr-badge-frueh">Frühbucher</span>}
                    </div>
                    <div className="wr-turnier-meta">
                      <span>📅 {formatDate(t.datum)}</span>
                      <span>📍 {t.ort}</span>
                      {t.disziplin && <span>🥋 {t.disziplin}</span>}
                    </div>
                    <div className="wr-turnier-preise">
                      <span>Einzel: <strong>{formatPreis(t.preis_einzel)}</strong></span>
                      <span>Team: <strong>{formatPreis(t.preis_team)}</strong></span>
                      {parseFloat(t.rabatt_zweite_kategorie || 0) > 0 &&
                        <span>2. Kat.: <strong>-{t.rabatt_zweite_kategorie}%</strong></span>}
                      {parseFloat(t.fruehbucher_rabatt || 0) > 0 && isFruh &&
                        <span className="wr-frueh-rabatt">Frühbucher: <strong>-{t.fruehbucher_rabatt}%</strong></span>}
                    </div>
                  </div>
                  {sel && divCnt > 0 && (
                    <span className="wr-turnier-div-badge">{divCnt} Division{divCnt > 1 ? 'en' : ''}</span>
                  )}
                </div>

                {/* Divisionen aufklappen wenn Turnier ausgewählt */}
                {sel && (
                  <div className="wr-div-section">
                    <div className="wr-div-section-header">
                      <button className="wr-div-toggle" onClick={() => setOpenTId(openTId === t.turnier_id ? null : t.turnier_id)}>
                        {openTId === t.turnier_id ? '▲' : '▼'} Divisionen auswählen
                      </button>
                      {openTId === t.turnier_id && (
                        <div className="wr-div-filter-col">
                          <div className="wr-div-tabs">
                            {DIV_TABS.filter(tab => {
                              if (tab.id === 'alle') return true;
                              return allDiv.some(d => tab.types.includes(d.Division_Type));
                            }).map(tab => {
                              const cnt = tab.types
                                ? athleteFilteredDiv.filter(d => tab.types.includes(d.Division_Type)).length
                                : athleteFilteredDiv.length;
                              return (
                                <button
                                  key={tab.id}
                                  className={`wr-div-tab ${activeTab === tab.id ? 'active' : ''}`}
                                  onClick={() => handleTabChange(tab.id)}
                                >
                                  {tab.label}
                                  <span className="wr-tab-cnt">{cnt}</span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Sub-Kategorie-Filter */}
                          {activeSubTabs && activeSubTabs.length > 2 && (
                            <div className="wr-sub-tabs">
                              {activeSubTabs.filter(sub => {
                                if (sub.id === 'alle') return true;
                                return athleteFilteredDiv.some(d => {
                                  if (activeTabDef.types && !activeTabDef.types.includes(d.Division_Type)) return false;
                                  return sub.match(d.Division_Name || '', d.Division_Type || '');
                                });
                              }).map(sub => {
                                const cnt = sub.id === 'alle'
                                  ? athleteFilteredDiv.filter(d => !activeTabDef.types || activeTabDef.types.includes(d.Division_Type)).length
                                  : athleteFilteredDiv.filter(d => {
                                      if (activeTabDef.types && !activeTabDef.types.includes(d.Division_Type)) return false;
                                      return sub.match(d.Division_Name || '', d.Division_Type || '');
                                    }).length;
                                return (
                                  <button
                                    key={sub.id}
                                    className={`wr-sub-tab ${subFilter === sub.id ? 'active' : ''}`}
                                    onClick={() => setSubFilter(sub.id)}
                                  >
                                    {sub.label}
                                    <span className="wr-tab-cnt">{cnt}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <div className="wr-search-wrap">
                            <span className="wr-search-icon">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                            </span>
                            <input className="wr-input wr-search-input" type="text"
                              placeholder="Division suchen…" value={search}
                              onChange={e => setSearch(e.target.value)} />
                          </div>
                        </div>
                      )}
                    </div>

                    {openTId === t.turnier_id && (
                      <div className="wr-div-list">
                        {filteredDiv.length === 0
                          ? <div className="wr-loading">Keine Divisionen gefunden.</div>
                          : filteredDiv.map(d => {
                              const norm    = normalize(d);
                              const sel_div = (divSelections[t.turnier_id] || []).some(s => s.id === norm.id);
                              const selDivs = divSelections[t.turnier_id] || [];
                              const anzEinzel = selDivs.filter(s => !(s.team || (s.name || '').toLowerCase().includes('team'))).length;
                              const preis = berechnePreis(norm, t, sel_div ? anzEinzel - 1 : anzEinzel);
                              const teamKey = `${t.turnier_id}__${norm.id}`;
                              const teamInfo = teamDaten[teamKey];
                              const duoTeamMode = isDuoOrTeam(norm);
                              return (
                                <div key={norm.id} className={`wr-div-row ${sel_div ? 'selected' : ''}`}
                                     onClick={() => handleDivClick(t.turnier_id, norm)}>
                                  <span className={`wr-checkbox ${sel_div ? 'checked' : ''}`}>{sel_div ? '✓' : ''}</span>
                                  <span className={`wr-typ-badge ${typClass(norm.typ)}`}>{norm.typ || '–'}</span>
                                  <span className="wr-div-name">
                                    {norm.name}
                                    {duoTeamMode && !sel_div && <span className="wr-team-hint">{duoTeamMode === 'duo' ? '👥 Duo' : '👥 Team'}</span>}
                                    {teamInfo && <span className="wr-team-info">👥 {teamInfo.teamName}{teamInfo.mitglieder?.length ? ` · ${teamInfo.mitglieder.join(', ')}` : ''}</span>}
                                  </span>
                                  <span className="wr-div-meta">
                                    {norm.alter      && <span>{norm.alter}</span>}
                                    {norm.geschlecht && <span>{norm.geschlecht}</span>}
                                    {norm.gewicht    && <span>{norm.gewicht}</span>}
                                    {norm.skill      && <span>{norm.skill}</span>}
                                  </span>
                                  <span className="wr-div-preis-live">{preis.toFixed(2).replace('.', ',')} €</span>
                                </div>
                              );
                            })
                        }
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Zusammenfassung gewählter Divisionen */}
      {selTurniere.length > 0 && selTurniere.some(t => (divSelections[t.turnier_id] || []).length > 0) && (
        <div className="wr-selection-bar">
          {selTurniere.map(t => {
            const divs = divSelections[t.turnier_id] || [];
            if (!divs.length) return null;
            return (
              <span key={t.turnier_id} className="wr-sel-turnier-sum">
                <strong>{t.name}:</strong> {divs.length} Division{divs.length > 1 ? 'en' : ''}
              </span>
            );
          })}
        </div>
      )}

      {/* Team/Duo Modal */}
      {teamModal && (
        <WrTeamModal
          divisionName={teamModal.div.name}
          mode={teamModal.mode}
          onConfirm={confirmTeamModal}
          onCancel={() => setTeamModal(null)}
        />
      )}
    </div>
  );
}

/* ── Athlet-Suche mit Autocomplete (für Team-Modal) ── */
function WrAthleteSearch({ placeholder, initialValue, onNameChange, onSelect }) {
  const [term, setTerm]       = useState(initialValue || '');
  const [results, setResults] = useState([]);
  const [busy, setBusy]       = useState(false);
  const [open, setOpen]       = useState(false);
  const timerRef              = useRef(null);

  const age = (d) => Math.floor((Date.now() - new Date(d)) / (365.25 * 864e5));

  const doSearch = async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/wettkaempfer?search=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const list = data.success ? data.data : Array.isArray(data) ? data : data.sportler || [];
        setResults(Array.isArray(list) ? list.slice(0, 8) : []);
      }
    } catch { setResults([]); }
    finally { setBusy(false); }
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setTerm(v);
    onNameChange(v);
    setOpen(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(v), 280);
  };

  const handleSelect = (s) => {
    const name = `${s.vorname} ${s.nachname}`;
    setTerm(name);
    onNameChange(name);
    onSelect && onSelect(s);
    setResults([]);
    setOpen(false);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div className="wr-athlete-search-wrap">
      <div className="wr-search-wrap">
        <span className="wr-search-icon">
          {busy
            ? <span className="wr-spin">⟳</span>
            : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          }
        </span>
        <input
          className="wr-input wr-search-input"
          type="text"
          value={term}
          placeholder={placeholder}
          onChange={handleInput}
          onFocus={() => { if (term.length >= 2) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </div>
      {open && results.length > 0 && (
        <div className="wr-dropdown">
          {results.map(s => (
            <div key={s.wettkaempfer_id || s.id} className="wr-dropdown-item" onMouseDown={() => handleSelect(s)}>
              <span className="wr-dropdown-name">
                {s.vorname} {s.nachname}
                <span className="wr-gender-tag">{s.geschlecht === 'männlich' || s.geschlecht === 'male' ? '♂' : '♀'}</span>
              </span>
              <span className="wr-dropdown-meta">
                {s.geburtsdatum && <span>{age(s.geburtsdatum)} J.</span>}
                {s.gewicht && <span>{s.gewicht} kg</span>}
                {s.verein_name && <span>{s.verein_name}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      {open && term.length >= 2 && !busy && results.length === 0 && (
        <div className="wr-dropdown wr-dropdown-empty">Kein Sportler gefunden</div>
      )}
    </div>
  );
}

/* ── Vereinfachtes Team-Modal für Wettkampftag-Registrierung ── */
function WrTeamModal({ divisionName, mode, onConfirm, onCancel }) {
  const isDuo = mode === 'duo';
  const [teamName, setTeamName] = useState('');
  // Track { name, wettkaempfer_id } per member
  const empty = () => ({ name: '', wettkaempfer_id: null });
  const [mitglieder, setMitglieder] = useState(isDuo ? [empty()] : [empty(), empty()]);

  const updateName = (i, name) => setMitglieder(prev =>
    prev.map((v, idx) => idx === i ? { ...v, name, wettkaempfer_id: null } : v)
  );
  const updateSelected = (i, s) => setMitglieder(prev =>
    prev.map((v, idx) => idx === i
      ? { name: `${s.vorname} ${s.nachname}`, wettkaempfer_id: s.wettkaempfer_id || s.id || null }
      : v)
  );
  const addMitglied    = () => setMitglieder(prev => [...prev, empty()]);
  const removeMitglied = (i) => setMitglieder(prev => prev.filter((_, idx) => idx !== i));

  const handleConfirm = () => {
    if (!teamName.trim()) { alert('Bitte Teamnamen eingeben.'); return; }
    const filled = mitglieder.filter(m => m.name.trim());
    if (isDuo && filled.length < 1) { alert('Bitte den Namen der 2. Person eingeben.'); return; }
    const names      = filled.map(m => m.name);
    const partnerIds = filled.map(m => m.wettkaempfer_id).filter(Boolean);
    onConfirm(teamName.trim(), names, partnerIds);
  };

  return ReactDOM.createPortal(
    <div className="wr-team-modal-overlay" onClick={onCancel}>
      <div className="wr-team-modal" onClick={e => e.stopPropagation()}>
        <div className="wr-team-modal-header">
          <h3>{isDuo ? '👥 Duo-Anmeldung' : '👥 Team-Anmeldung'}</h3>
          <span className="wr-team-modal-div">{divisionName}</span>
        </div>
        <div className="wr-team-modal-body">
          <label className="wr-team-modal-label">
            Teamname <span style={{ color: '#f87171' }}>*</span>
            <input
              className="wr-input"
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
              placeholder={isDuo ? 'z.B. Müller / Schmidt' : 'z.B. Dragons Team A'}
              autoFocus
            />
          </label>
          <div className="wr-team-members-title">
            {isDuo ? '2. Person' : 'Weitere Teammitglieder'}
          </div>
          {mitglieder.map((m, i) => (
            <div key={i} className="wr-team-member-row">
              <span className="wr-team-member-nr">{i + 2}.</span>
              <WrAthleteSearch
                placeholder={`Name Person ${i + 2} suchen…`}
                initialValue={m.name}
                onNameChange={val => updateName(i, val)}
                onSelect={s => updateSelected(i, s)}
              />
              {m.wettkaempfer_id && (
                <span className="wr-partner-id-badge" title={`ID: ${m.wettkaempfer_id}`}>✓</span>
              )}
              {!isDuo && mitglieder.length > 1 && (
                <button className="wr-team-member-remove" onClick={() => removeMitglied(i)}>✕</button>
              )}
            </div>
          ))}
          {!isDuo && (
            <button className="wr-team-add" onClick={addMitglied}>+ Weitere Person</button>
          )}
        </div>
        <div className="wr-team-modal-footer">
          <button className="wr-btn-ghost" onClick={onCancel}>Abbrechen</button>
          <button className="wr-btn-primary" onClick={handleConfirm}>Bestätigen ✓</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────────
   Schritt 3 — Warenkorb
   ───────────────────────────────────────────────── */
function WarenkorbStep({ warenkorb, pendingProfil, pendingAnmeldungen, hasPending, onRemove, onAddPending, onAddAnother, gesamtpreis }) {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '';

  return (
    <div className="wr-step-body">
      {/* Noch nicht hinzugefügt */}
      {hasPending && (
        <div className="wr-wk-pending-card">
          <div className="wr-wk-pending-label">Noch nicht hinzugefügt</div>
          <div className="wr-wk-pending-content">
            <span className="wr-wk-name">{pendingProfil.vorname} {pendingProfil.nachname}</span>
            {pendingAnmeldungen.map(a => (
              <span key={a.turnier.turnier_id} className="wr-wk-divs">
                {a.turnier.name}: {a.divisionen.map(d => d.name).join(', ')}
              </span>
            ))}
          </div>
          {onAddPending && (
            <button className="wr-btn-add-pending" onClick={onAddPending}>+ Zum Warenkorb</button>
          )}
        </div>
      )}

      {warenkorb.length === 0 ? (
        <div className="wr-wk-empty">
          <div className="wr-wk-empty-icon">🛒</div>
          <p>Noch keine Kämpfer im Warenkorb.</p>
          <p className="wr-muted">Gehe zurück zu Schritt 1.</p>
        </div>
      ) : (
        <div className="wr-card">
          <div className="wr-card-label">{warenkorb.length} Kämpfer im Warenkorb</div>
          <div className="wr-wk-entries">
            {warenkorb.map((entry, idx) => {
              const summe = entry.anmeldungen.reduce((s, a) =>
                s + a.divisionen.reduce((ss, d) => ss + d.preis, 0), 0);
              return (
                <div key={idx} className="wr-wk-entry">
                  <div className="wr-wk-entry-top">
                    <div className="wr-wk-entry-name">
                      {entry.profil.vorname} {entry.profil.nachname}
                      {entry.profil.geburtsdatum && (
                        <span className="wr-wk-entry-age">
                          {Math.floor((Date.now() - new Date(entry.profil.geburtsdatum)) / (365.25 * 864e5))} J.
                        </span>
                      )}
                      {entry.profil.geschlecht && (
                        <span className="wr-gender-tag">
                          {entry.profil.geschlecht === 'männlich' ? '♂' : '♀'}
                        </span>
                      )}
                    </div>
                    <button className="wr-wk-remove" onClick={() => onRemove(idx)}>✕</button>
                  </div>

                  {entry.anmeldungen.map(a => (
                    <div key={a.turnier.turnier_id} className="wr-wk-turnier-block">
                      <div className="wr-wk-turnier-name">
                        📅 {a.turnier.name} — {formatDate(a.turnier.datum)}, {a.turnier.ort}
                      </div>
                      <div className="wr-wk-divs-list">
                        {a.divisionen.map((d, di) => (
                          <span key={di} className="wr-wk-div-tag">
                            <span className="wr-typ-dot kumite"></span>
                            {d.name}
                            <span className="wr-wk-div-price">{d.preis.toFixed(2).replace('.', ',')} €</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="wr-wk-entry-summe">Summe: <strong>{summe.toFixed(2).replace('.', ',')} €</strong></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="wr-wk-bottom">
        <button className="wr-btn-ghost" onClick={onAddAnother}>+ Weiteren Kämpfer</button>
        {warenkorb.length > 0 && (
          <div className="wr-wk-total">
            Gesamt: <strong>{gesamtpreis.toFixed(2).replace('.', ',')} €</strong>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   Schritt 4 — Abschluss
   ───────────────────────────────────────────────── */
function AbschlussStep({ warenkorb, gesamtpreis, submitResult, onNewFighter }) {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '–';

  // Erfolgreich abgeschlossen
  if (submitResult && submitResult.errors.length === 0) {
    return (
      <div className="wr-step-body">
        <div className="wr-submit-success">
          <div className="wr-success-icon">✓</div>
          <h3>Registrierung erfolgreich!</h3>
          <p>{submitResult.ok.length} Anmeldung{submitResult.ok.length !== 1 ? 'en' : ''} wurden gespeichert.</p>
          <div className="wr-success-list">
            {submitResult.ok.map((r, i) => (
              <div key={i} className="wr-success-row">
                <strong>{r.name}</strong> → {r.turnier}
                <span className="wr-success-cnt">{r.divisionen} Division{r.divisionen !== 1 ? 'en' : ''}</span>
              </div>
            ))}
          </div>
          {onNewFighter && (
            <button className="wr-btn-primary" onClick={onNewFighter} style={{ marginTop: '1.5rem' }}>
              + Neuen Kämpfer registrieren
            </button>
          )}
        </div>
      </div>
    );
  }

  // Teilweise Fehler
  if (submitResult && submitResult.errors.length > 0) {
    return (
      <div className="wr-step-body">
        {submitResult.ok.length > 0 && (
          <div className="wr-submit-success wr-submit-partial">
            <div className="wr-success-icon partial">✓</div>
            <p>{submitResult.ok.length} Anmeldung{submitResult.ok.length !== 1 ? 'en' : ''} erfolgreich.</p>
          </div>
        )}
        <div className="wr-submit-errors">
          <div className="wr-err-label">Fehler bei {submitResult.errors.length} Kämpfer{submitResult.errors.length !== 1 ? 'n' : ''}:</div>
          {submitResult.errors.map((e, i) => (
            <div key={i} className="wr-err-row">
              <strong>{e.name}</strong>: {e.error}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="wr-step-body">
      {warenkorb.map((entry, idx) => (
        <div key={idx} className="wr-card">
          <div className="wr-card-label">
            Kämpfer {idx + 1}: {entry.profil.vorname} {entry.profil.nachname}
          </div>
          <div className="wr-summary-rows">
            <div className="wr-summary-row">
              <span>Geburtsdatum</span><span>{formatDate(entry.profil.geburtsdatum)}</span>
            </div>
            <div className="wr-summary-row">
              <span>Geschlecht</span><span>{entry.profil.geschlecht || '–'}</span>
            </div>
            {entry.profil.email && (
              <div className="wr-summary-row"><span>E-Mail</span><span>{entry.profil.email}</span></div>
            )}
          </div>
          {entry.anmeldungen.map(a => (
            <div key={a.turnier.turnier_id} className="wr-summary-turnier-block">
              <div className="wr-summary-turnier-title">
                {a.turnier.name} — {formatDate(a.turnier.datum)} — {a.turnier.ort}
              </div>
              {a.divisionen.map((d, di) => (
                <div key={di} className="wr-summary-div-row">
                  <span className={`wr-typ-badge ${d.typ ? d.typ.toLowerCase().includes('form') || d.typ.toLowerCase().includes('kata') ? 'kata' : 'kumite' : 'sonstige'}`}>{d.typ || '–'}</span>
                  <span>{d.name}</span>
                  <span className="wr-div-price">{d.preis.toFixed(2).replace('.', ',')} €</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
      {warenkorb.length > 0 && (
        <div className="wr-final-total">
          <span>Gesamtbetrag ({warenkorb.length} Kämpfer)</span>
          <strong>{gesamtpreis.toFixed(2).replace('.', ',')} €</strong>
        </div>
      )}
    </div>
  );
}
